import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Button, Card, Col, Form, Input, InputNumber, Modal, Popconfirm,
  Row, Select, Space, Table, Tabs, Tag, Typography,
} from "antd";
import {
  CopyOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, FileTextOutlined,
  PlusOutlined, SendOutlined, UploadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import billingApi from "../../helpers/billingApi";

const money = (value) => `${new Intl.NumberFormat("vi-VN").format(value || 0)}đ`;
const errorText = (error) => error.response?.status === 401
  ? "Phiên quản trị không còn hợp lệ. Vui lòng đăng xuất và đăng nhập lại."
  : error.response?.data?.error || "Không thể kết nối với dịch vụ hóa đơn";

function BillingAdmin() {
  const [items, setItems] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("bills");
  const [createdBill, setCreatedBill] = useState(null);
  const [linkNotice, setLinkNotice] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [billForm] = Form.useForm();
  const [itemForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const watchedLines = Form.useWatch("lines", billForm) || [];
  const watchedDiscount = Form.useWatch("discount", billForm) || 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemResult, billResult] = await Promise.all([
        billingApi.get("/admin/billing/items"),
        billingApi.get("/admin/billing/bills"),
      ]);
      setItems(itemResult.data.items);
      setBills(billResult.data.bills);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subtotal = watchedLines.reduce((sum, line) => {
    const item = items.find((product) => product.id === line?.itemId);
    return sum + (item?.price || 0) * (line?.quantity || 0);
  }, 0);

  const importItems = async (payload) => {
    setSaving(true);
    try {
      const response = await billingApi.post("/admin/billing/items/import", payload);
      toast.success(`Đã nhập ${response.data.imported} sản phẩm dùng cho hóa đơn`);
      await load();
      return true;
    } catch (error) {
      toast.error(errorText(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 900000) return toast.error("Tệp CSV phải nhỏ hơn 900 KB");
    await importItems({ csv: await file.text() });
  };

  const createBill = async (values) => {
    setSaving(true);
    try {
      const response = await billingApi.post("/admin/billing/bills", {
        ...values,
        discount: values.discount || 0,
        lines: values.lines.map((line) => ({ itemId: line.itemId, quantity: line.quantity })),
      });
      setCreatedBill(response.data.bill);
      setLinkNotice(null);
      billForm.resetFields();
      toast.success(`Đã tạo ${response.data.bill.number}`);
      await load();
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async (bill) => {
    if (!bill.publicUrl) return toast.error("Chưa có liên kết hóa đơn công khai");
    try {
      await navigator.clipboard.writeText(bill.publicUrl);
      toast.success("Đã sao chép liên kết. Hãy dán vào cuộc trò chuyện Zalo của khách.");
    } catch {
      toast.error("Không thể sao chép. Hãy mở hóa đơn và sao chép địa chỉ trang.");
    }
  };

  const downloadPdf = async (bill) => {
    setSaving(true);
    try {
      const response = await billingApi.get(`/admin/billing/bills/${bill.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${bill.number}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Đã tải PDF. Bạn có thể gửi tệp này qua Zalo cho khách.");
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  const sendZalo = async (bill) => {
    setSaving(true);
    try {
      await billingApi.post(`/admin/billing/bills/${bill.id}/send-zalo`, {});
      toast.success("Zalo đã chấp nhận tin nhắn hóa đơn");
      await load();
    } catch (error) {
      toast.error(errorText(error));
      await load();
    } finally {
      setSaving(false);
    }
  };

  const revokeLink = async (bill) => {
    setSaving(true);
    try {
      const response = await billingApi.post(`/admin/billing/bills/${bill.id}/revoke`, {});
      if (selectedBill?.id === bill.id) setSelectedBill(response.data.bill);
      if (createdBill?.id === bill.id) setCreatedBill(response.data.bill);
      if (linkNotice?.id === bill.id) setLinkNotice(null);
      toast.success("Đã thu hồi. Liên kết cũ không còn mở được hóa đơn.");
      await load();
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  const rotateLink = async (bill) => {
    setSaving(true);
    try {
      const response = await billingApi.post(`/admin/billing/bills/${bill.id}/rotate-link`, {});
      const replacement = response.data.bill;
      setLinkNotice(replacement);
      if (selectedBill?.id === bill.id) setSelectedBill(replacement);
      if (createdBill?.id === bill.id) setCreatedBill(null);
      toast.success("Đã thay liên kết. Liên kết cũ không còn sử dụng được.");
      await load();
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  const archiveItem = async (item) => {
    try {
      await billingApi.delete(`/admin/billing/items/${item.id}`);
      toast.success("Đã ẩn sản phẩm khỏi danh sách tạo hóa đơn");
      await load();
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  const saveEdit = async (values) => {
    try {
      await billingApi.put(`/admin/billing/items/${editingItem.id}`, values);
      setEditingItem(null);
      toast.success("Đã cập nhật giá sản phẩm");
      await load();
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  const itemOptions = items.map((item) => ({
    value: item.id, label: `${item.name} · ${item.sku} · ${money(item.price)}/${item.unit}`,
  }));

  const itemColumns = [
    { title: "Mã", dataIndex: "sku", key: "sku" },
    { title: "Tên sản phẩm", dataIndex: "name", key: "name" },
    { title: "Đơn vị", dataIndex: "unit", key: "unit" },
    { title: "Đơn giá", dataIndex: "price", key: "price", render: money },
    { title: "Thao tác", key: "actions", render: (_, item) => (
      <Space>
        <Button icon={<EditOutlined />} onClick={() => { setEditingItem(item); editForm.setFieldsValue(item); }}>Sửa</Button>
        <Popconfirm title="Ẩn sản phẩm này?" description="Hóa đơn đã tạo vẫn giữ giá cũ." onConfirm={() => archiveItem(item)}>
          <Button danger icon={<DeleteOutlined />}>Ẩn</Button>
        </Popconfirm>
      </Space>
    ) },
  ];

  const billColumns = [
    { title: "Số hóa đơn", dataIndex: "number", key: "number", render: (number, bill) => <Button type="link" onClick={() => setSelectedBill(bill)}>{number}</Button> },
    { title: "Khách hàng", dataIndex: "customerName", key: "customerName" },
    { title: "Ngày dịch vụ", dataIndex: "serviceDate", key: "serviceDate" },
    { title: "Tổng cộng", dataIndex: "total", key: "total", render: money },
    { title: "Zalo OA", key: "delivery", render: (_, bill) => {
      const latest = bill.deliveries.find((delivery) => delivery.channel === "zalo_template");
      return latest ? <Tag color={latest.status === "accepted" ? "green" : "red"}>{latest.status === "accepted" ? "Đã chấp nhận" : "Gửi thất bại"}</Tag> : <Tag>Chưa gửi</Tag>;
    } },
    { title: "Thao tác", key: "actions", render: (_, bill) => (
      <Space wrap>
        <Button icon={<DownloadOutlined />} disabled={saving} onClick={() => downloadPdf(bill)}>Tải PDF</Button>
        <Button icon={<CopyOutlined />} disabled={!bill.publicUrl} onClick={() => copyLink(bill)}>Sao chép link</Button>
        <Button icon={<FileTextOutlined />} disabled={!bill.publicUrl} onClick={() => window.open(bill.publicUrl, "_blank", "noopener,noreferrer")}>Xem</Button>
        <Button icon={<SendOutlined />} disabled={!bill.zaloUserId || !bill.publicUrl || saving} onClick={() => sendZalo(bill)}>Gửi Zalo OA</Button>
        <Popconfirm title="Thu hồi liên kết này?" description="Khách và bất kỳ ai có liên kết cũ sẽ không thể mở nó nữa." onConfirm={() => revokeLink(bill)}>
          <Button danger disabled={!bill.publicUrl || saving}>Thu hồi link</Button>
        </Popconfirm>
        <Popconfirm title="Tạo liên kết thay thế?" description="Liên kết cũ sẽ ngừng hoạt động. Hãy gửi liên kết mới riêng cho khách." onConfirm={() => rotateLink(bill)}>
          <Button disabled={saving}>Thay link</Button>
        </Popconfirm>
      </Space>
    ) },
  ];

  const productFields = <>
    <Form.Item name="sku" label="Mã sản phẩm" rules={[{ required: true, message: "Nhập mã sản phẩm" }]}><Input maxLength={80} /></Form.Item>
    <Form.Item name="name" label="Tên sản phẩm" rules={[{ required: true, message: "Nhập tên sản phẩm" }]}><Input maxLength={200} /></Form.Item>
    <Form.Item name="unit" label="Đơn vị"><Input maxLength={40} placeholder="cái" /></Form.Item>
    <Form.Item name="price" label="Đơn giá (VND)" rules={[{ required: true, message: "Nhập đơn giá" }]}><InputNumber min={0} precision={0} style={{ width: "100%" }} /></Form.Item>
  </>;

  return <div style={{ padding: "24px" }}>
    <Typography.Title level={2}>Hóa đơn dịch vụ</Typography.Title>
    <Typography.Paragraph style={{ color: "#cbd5e1" }}>Sản phẩm ở đây chỉ dùng để lập hóa đơn, không xuất hiện trên trang bán hàng.</Typography.Paragraph>
    <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
      { key: "bills", label: <span style={{ color: "#e2e8f0" }}>Lập và gửi hóa đơn</span>, children: <>
        {linkNotice?.publicUrl && <Alert style={{ marginBottom: 20 }} type="warning" showIcon
          message={`Liên kết mới cho ${linkNotice.number}`}
          description={<Space wrap><span>Liên kết cũ đã ngừng hoạt động. Gửi liên kết mới riêng cho khách hàng.</span><Button icon={<CopyOutlined />} onClick={() => copyLink(linkNotice)}>Sao chép link mới</Button></Space>} />}
        {createdBill?.publicUrl && <Alert style={{ marginBottom: 20 }} type="success" showIcon
          message={`Đã tạo ${createdBill.number}`}
          description={<Space wrap><span>Tải PDF để gửi tệp qua Zalo, hoặc sao chép liên kết hóa đơn.</span><Button icon={<DownloadOutlined />} onClick={() => downloadPdf(createdBill)}>Tải PDF</Button><Button icon={<CopyOutlined />} onClick={() => copyLink(createdBill)}>Sao chép link</Button><Button onClick={() => window.open("https://chat.zalo.me/", "_blank", "noopener,noreferrer")}>Mở Zalo</Button></Space>} />}
        <Card title="Hóa đơn cho lần dịch vụ này" style={{ marginBottom: 24 }}>
          <Form form={billForm} layout="vertical" onFinish={createBill}
            initialValues={{ serviceDate: new Date().toISOString().slice(0, 10), discount: 0, lines: [{ quantity: 1 }] }}>
            <Row gutter={16}>
              <Col xs={24} md={12}><Form.Item name="customerName" label="Tên khách hàng" rules={[{ required: true, message: "Nhập tên khách hàng" }]}><Input maxLength={200} /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="customerPhone" label="Số điện thoại"><Input maxLength={30} /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="zaloUserId" label="Zalo OA user ID (để gửi tự động)" tooltip="Lấy từ tương tác của khách với Official Account. Số điện thoại không thay thế được ID này."><Input maxLength={80} /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="serviceDate" label="Ngày dịch vụ" rules={[{ required: true, message: "Chọn ngày dịch vụ" }]}><Input type="date" /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="vehicle" label="Loại xe"><Input maxLength={200} /></Form.Item></Col>
              <Col xs={24} md={12}><Form.Item name="plate" label="Biển số xe"><Input maxLength={40} /></Form.Item></Col>
            </Row>
            <div style={{ color: "#172033", fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Hạng mục trên hóa đơn</div>
            <Form.List name="lines">{(fields, { add, remove }) => <>
              {fields.map((field) => <Row key={field.key} gutter={12} align="middle">
                <Col xs={24} md={15}><Form.Item name={[field.name, "itemId"]} label="Sản phẩm / dịch vụ" rules={[{ required: true, message: "Chọn hạng mục" }]}><Select showSearch optionFilterProp="label" options={itemOptions} placeholder="Chọn từ danh sách riêng cho hóa đơn" /></Form.Item></Col>
                <Col xs={18} md={6}><Form.Item name={[field.name, "quantity"]} label="Số lượng" rules={[{ required: true, message: "Nhập số lượng" }]}><InputNumber min={1} max={10000} precision={0} style={{ width: "100%" }} /></Form.Item></Col>
                <Col xs={6} md={3}><Button danger disabled={fields.length === 1} onClick={() => remove(field.name)}>Xóa</Button></Col>
              </Row>)}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ quantity: 1 })}>Thêm hạng mục</Button>
            </>}</Form.List>
            <Row gutter={16} style={{ marginTop: 20 }}>
              <Col xs={24} md={8}><Form.Item name="discount" label="Giảm giá (VND)"><InputNumber min={0} precision={0} style={{ width: "100%" }} /></Form.Item></Col>
              <Col xs={24} md={16}><Form.Item name="notes" label="Ghi chú"><Input.TextArea rows={2} maxLength={1000} /></Form.Item></Col>
            </Row>
            <Typography.Paragraph strong>Tạm tính: {money(subtotal)} · Tổng sau giảm giá: {money(Math.max(0, subtotal - watchedDiscount))}</Typography.Paragraph>
            <Button type="primary" htmlType="submit" loading={saving} disabled={!items.length}>Tạo hóa đơn</Button>
            {!items.length && <Typography.Text style={{ marginLeft: 12 }}>Hãy nhập sản phẩm ở tab bên cạnh trước.</Typography.Text>}
          </Form>
        </Card>
        <Card title="Hóa đơn gần đây" extra={<Button onClick={load}>Tải lại</Button>}><Table rowKey="id" loading={loading} columns={billColumns} dataSource={bills} scroll={{ x: 1450 }} pagination={{ pageSize: 10 }} /></Card>
      </> },
      { key: "items", label: <span style={{ color: "#e2e8f0" }}>Sản phẩm dùng cho hóa đơn</span>, children: <>
        <Row gutter={16}>
          <Col xs={24} lg={12}><Card title="Thêm một sản phẩm" style={{ marginBottom: 20 }}><Form form={itemForm} layout="vertical" onFinish={async (values) => { if (await importItems({ items: [values] })) itemForm.resetFields(); }}>{productFields}<Button type="primary" htmlType="submit" loading={saving}>Lưu sản phẩm</Button></Form></Card></Col>
          <Col xs={24} lg={12}><Card title="Nhập từ CSV" style={{ marginBottom: 20 }}><Typography.Paragraph>CSV cần các cột <code>sku,name,price,unit</code>. Giá là số nguyên VND. Mã trùng sẽ cập nhật sản phẩm trong danh sách này.</Typography.Paragraph><Button icon={<UploadOutlined />} loading={saving} onClick={() => document.getElementById("billing-csv-file").click()}>Chọn tệp CSV</Button><input id="billing-csv-file" type="file" accept=".csv,text/csv" onChange={handleCsv} style={{ display: "none" }} /></Card></Col>
        </Row>
        <Card title="Danh sách sản phẩm riêng cho hóa đơn" extra={<Button onClick={load}>Tải lại</Button>}><Table rowKey="id" loading={loading} columns={itemColumns} dataSource={items} scroll={{ x: 680 }} pagination={{ pageSize: 10 }} /></Card>
      </> },
    ]} />
    <Modal title="Sửa sản phẩm và giá" open={!!editingItem} onCancel={() => setEditingItem(null)} onOk={() => editForm.submit()} okText="Lưu" destroyOnHidden>
      <Form form={editForm} layout="vertical" onFinish={saveEdit}>{productFields}</Form>
    </Modal>
    <Modal title={selectedBill?.number} open={!!selectedBill} onCancel={() => setSelectedBill(null)} footer={null} width={740} destroyOnHidden>
      {selectedBill && <>
        <Typography.Paragraph>Khách: {selectedBill.customerName} · {selectedBill.customerPhone || "—"}<br />Xe: {selectedBill.vehicle || "—"} · {selectedBill.plate || "—"}<br />Ngày dịch vụ: {selectedBill.serviceDate}</Typography.Paragraph>
        <Table rowKey={(_, index) => index} pagination={false} dataSource={selectedBill.lines} columns={[
          { title: "Hạng mục", dataIndex: "name" }, { title: "Số lượng", dataIndex: "quantity" },
          { title: "Đơn giá", dataIndex: "unitPrice", render: money }, { title: "Thành tiền", dataIndex: "lineTotal", render: money },
        ]} scroll={{ x: 560 }} />
        <Typography.Paragraph style={{ marginTop: 16 }} strong>Giảm giá: {money(selectedBill.discount)} · Tổng cộng: {money(selectedBill.total)}</Typography.Paragraph>
        <Space wrap>
          <Button icon={<DownloadOutlined />} onClick={() => downloadPdf(selectedBill)}>Tải PDF</Button>
          <Button disabled={!selectedBill.publicUrl} onClick={() => copyLink(selectedBill)}>Sao chép link</Button>
          <Button disabled={!selectedBill.publicUrl} onClick={() => window.open(selectedBill.publicUrl, "_blank", "noopener,noreferrer")}>Xem hóa đơn</Button>
          <Popconfirm title="Thu hồi liên kết này?" onConfirm={() => revokeLink(selectedBill)}><Button danger disabled={!selectedBill.publicUrl || saving}>Thu hồi link</Button></Popconfirm>
          <Popconfirm title="Tạo liên kết thay thế?" onConfirm={() => rotateLink(selectedBill)}><Button disabled={saving}>Thay link</Button></Popconfirm>
        </Space>
      </>}
    </Modal>
  </div>;
}

export default BillingAdmin;
